"use client";

import { Tag, Alert } from "@/ui";
import { colors, spacing, radius, textStyles, typography, primitives } from "@/ui/tokens";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

export function JudgeResultDisplay({ toolCall }: ToolDisplayProps) {
  const judgeOutput = toolCall.finalOutput;

  if (!judgeOutput || typeof judgeOutput !== 'object') {
    return null;
  }

  const judgeResult = judgeOutput as {
    overallScore: number;
    conversationState: string;
    metrics: {
      intentClarity: { score: number; reason: string; suggestion: string };
      dataAvailability: { score: number; reason: string; suggestion: string };
      toolRelevance: { score: number; reason: string; suggestion: string };
      actionCompleteness: { score: number; reason: string; suggestion: string };
      informationAccuracy: { score: number; reason: string; suggestion: string };
      responseUtility: { score: number; reason: string; suggestion: string };
      contextRetention: { score: number; reason: string; suggestion: string };
      processEfficiency: { score: number; reason: string; suggestion: string };
    };
    priorityActions: string[];
    missingContextSummary: string | null;
    progressTowardsGoal: number;
    confidence: number;
    blockers: string[];
    recommendedNextSteps: string[];
    shouldContinue: boolean;
    debugMode: boolean;
    availableTools: string[];
  };

  const {
    overallScore,
    conversationState,
    metrics,
    priorityActions,
    missingContextSummary,
    progressTowardsGoal,
    confidence,
    blockers,
    recommendedNextSteps,
    shouldContinue,
    availableTools,
    debugMode,
  } = judgeResult;

  const getScoreColor = (score: number) => {
    if (score >= 7) return colors.signal.success;
    if (score >= 5) return colors.signal.warning;
    return colors.signal.danger;
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 7) return colors.signal.successLight;
    if (score >= 5) return colors.signal.warningLight;
    return colors.signal.dangerLight;
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case 'ready_to_respond':
        return colors.signal.success;
      case 'on_track':
        return colors.signal.info;
      case 'needs_improvement':
        return colors.signal.warning;
      case 'stuck':
        return colors.signal.danger;
      default:
        return primitives.neutral[500];
    }
  };

  const getStateLabel = (state: string) => {
    switch (state) {
      case 'ready_to_respond':
        return 'Ready to Respond';
      case 'on_track':
        return 'On Track';
      case 'needs_improvement':
        return 'Needs Improvement';
      case 'stuck':
        return 'Stuck';
      default:
        return state;
    }
  };

  return (
    <div style={{ padding: spacing[5] }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing[5] }}>
        <h3 style={{ ...textStyles.subtitle1, fontWeight: 600, color: colors.text.primary, margin: 0 }}>
          Conversation Judge
        </h3>
        <div
          style={{
            paddingLeft: spacing[4],
            paddingRight: spacing[4],
            paddingTop: spacing[2],
            paddingBottom: spacing[2],
            borderRadius: radius.md,
            backgroundColor: getStateColor(conversationState),
            color: colors.text.inverse,
          }}
        >
          <span style={{ ...textStyles.body2, color: colors.text.inverse, fontWeight: 500 }}>
            {getStateLabel(conversationState)}
          </span>
        </div>
      </div>

      {/* Score Cards Row */}
      <div style={{ display: 'flex', gap: spacing[5], marginBottom: spacing[5] }}>
        {/* Overall Score */}
        <div
          style={{
            flex: 1,
            backgroundColor: colors.canvas.default,
            borderRadius: radius.md,
            padding: spacing[4],
          }}
        >
          <span style={{ ...textStyles.label, marginBottom: spacing[2], color: colors.text.muted, fontWeight: 600, display: "block" }}>
            Overall Score
          </span>
          <span style={{ ...textStyles.h1, fontWeight: 700, color: getScoreColor(overallScore), display: "block" }}>
            {overallScore.toFixed(1)}
          </span>
          {shouldContinue && (
            <span style={{ ...textStyles.caption, marginTop: spacing[2], color: colors.signal.warning, display: "block" }}>
              Continue working (score &lt; 7.0)
            </span>
          )}
          {!shouldContinue && (
            <span style={{ ...textStyles.caption, marginTop: spacing[2], color: colors.signal.success, display: "block" }}>
              Ready to respond to user
            </span>
          )}
        </div>

        {/* Progress */}
        <div
          style={{
            flex: 1,
            backgroundColor: colors.canvas.default,
            borderRadius: radius.md,
            padding: spacing[4],
          }}
        >
          <span style={{ ...textStyles.label, marginBottom: spacing[2], color: colors.text.muted, fontWeight: 600, display: "block" }}>
            Progress
          </span>
          <div
            style={{
              width: '100%',
              height: 8,
              backgroundColor: primitives.neutral[200],
              borderRadius: radius.sm,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${progressTowardsGoal * 100}%`,
                height: '100%',
                backgroundColor: colors.action.product,
                transition: 'all 300ms ease-in-out',
              }}
            />
          </div>
          <span style={{ ...textStyles.body2, marginTop: spacing[2], color: colors.text.primary, display: "block" }}>
            {(progressTowardsGoal * 100).toFixed(0)}%
          </span>
        </div>

        {/* Confidence */}
        <div
          style={{
            flex: 1,
            backgroundColor: colors.canvas.default,
            borderRadius: radius.md,
            padding: spacing[4],
          }}
        >
          <span style={{ ...textStyles.label, marginBottom: spacing[2], color: colors.text.muted, fontWeight: 600, display: "block" }}>
            Confidence
          </span>
          <span style={{ ...textStyles.h1, color: colors.text.primary, display: "block" }}>
            {(confidence * 100).toFixed(0)}%
          </span>
        </div>
      </div>

      {/* Missing Context Alert */}
      {missingContextSummary && (
        <div style={{ marginBottom: spacing[5] }}>
          <Alert variant="warning">
            Missing Context: {missingContextSummary}
          </Alert>
        </div>
      )}

      {/* Blockers Alert */}
      {blockers.length > 0 && (
        <div style={{ marginBottom: spacing[5] }}>
          <Alert variant="error">
            <div>
              <span style={{ ...textStyles.body2, fontWeight: 600, marginBottom: spacing[2], display: "block" }}>Blockers:</span>
              <ul style={{ paddingLeft: spacing[4], margin: 0 }}>
                {blockers.map((blocker, idx) => (
                  <li key={idx}>{blocker}</li>
                ))}
              </ul>
            </div>
          </Alert>
        </div>
      )}

      {/* Priority Actions */}
      {priorityActions.length > 0 && (
        <div style={{ marginBottom: spacing[5], padding: spacing[4], backgroundColor: colors.signal.infoLight, borderRadius: radius.md }}>
          <span style={{ ...textStyles.body2, fontWeight: 600, marginBottom: spacing[2], display: "block" }}>
            Priority Actions
          </span>
          <ul style={{ paddingLeft: spacing[4], margin: 0 }}>
            {priorityActions.map((action, idx) => (
              <li key={idx}>{action}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommended Next Steps */}
      <div style={{ marginTop: spacing[6] }}>
        <span style={{ ...textStyles.body2, fontWeight: 600, marginBottom: spacing[4], display: "block" }}>
          Recommended Next Steps
        </span>
        <div style={{ padding: spacing[4], backgroundColor: colors.canvas.default, borderRadius: radius.md }}>
          <ul style={{ paddingLeft: spacing[4], margin: 0 }}>
            {recommendedNextSteps.map((step, idx) => (
              <li key={idx}>{step}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div style={{ marginTop: spacing[6] }}>
        <span style={{ ...textStyles.body2, fontWeight: 600, marginBottom: spacing[4], display: "block" }}>
          Detailed Metrics
        </span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: spacing[4] }}>
          {Object.entries(metrics).map(([key, metric]) => (
            <div
              key={key}
              style={{
                backgroundColor: colors.canvas.default,
                borderRadius: radius.md,
                padding: spacing[4],
                border: `2px solid ${getScoreBgColor(metric.score)}`,
              }}
            >
              <span style={{ ...textStyles.body2, fontWeight: 600, marginBottom: spacing[2], textTransform: 'capitalize', display: "block" }}>
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing[4] }}>
                <span style={{ ...textStyles.body1, color: getScoreColor(metric.score), fontWeight: 700 }}>
                  {metric.score.toFixed(1)}/10
                </span>
              </div>
              <Tag
                size="sm"
                color={metric.score >= 8 ? 'success' : metric.score >= 6 ? 'warning' : 'danger'}
              >
                {metric.score >= 8 ? 'High' : metric.score >= 6 ? 'Medium' : 'Low'}
              </Tag>
              <p style={{ ...textStyles.caption, color: colors.text.muted, margin: 0, marginTop: spacing[2] }}>
                {metric.reason}
              </p>
              {metric.suggestion && (
                <div style={{ marginTop: spacing[2] }}>
                  <Alert variant="info">
                    <span style={{ ...textStyles.body2, color: colors.signal.info }}>
                      {metric.suggestion}
                    </span>
                  </Alert>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default JudgeResultDisplay;
